import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

export default function HeroSlider({ slides }) {
  const { lang } = useLang();
  const ar = lang === "ar";
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides.length;
  const touch = useRef(null);
  const go = useCallback((d) => { setPaused(true); setI((p) => (p + d + n) % n); }, [n]);

  useEffect(() => {
    slides.forEach((s) => { if (s.images?.[0]) { const im = new Image(); im.src = resolveImg(s.images[0]); } });
  }, [slides]);

  useEffect(() => {
    if (n <= 1 || paused) return;
    const id = setInterval(() => setI((p) => (p + 1) % n), 4500);
    return () => clearInterval(id);
  }, [n, i, paused]);

  if (n === 0) return <div className="h-[70vh] min-h-[480px] bg-[#6B563F] animate-pulse" />;

  const cur = slides[i];
  return (
    <section
      className="relative h-[78vh] min-h-[520px] max-h-[860px] overflow-hidden bg-[#6B563F]"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touch.current == null) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        if (Math.abs(dx) > 50) go(dx > 0 ? 1 : -1);
        touch.current = null;
      }}
      data-testid="hero-slider"
    >
      {slides.map((s, idx) => (
        <div key={s.id} aria-hidden={idx !== i}
          style={{ position: "absolute", inset: 0, opacity: idx === i ? 1 : 0,
                   transition: idx === i ? "opacity 700ms ease" : "none",
                   zIndex: idx === i ? 2 : 1, pointerEvents: idx === i ? "auto" : "none" }}>
          <img src={resolveImg(s.images[0])} alt={s.name_ar}
            width="1200" height="1600"
            loading={idx === 0 ? "eager" : "lazy"} fetchPriority={idx === 0 ? "high" : "auto"} decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 18%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141110]/75 via-[#141110]/18 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full max-w-6xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-20">
        <span className="inline-block self-start text-[13px] md:text-[15px] font-semibold mb-6 md:mb-8 bg-[#F9F6F0] text-[#141110] px-4 py-1.5"
          style={{ letterSpacing: "0.04em" }}>
          {ar ? "رنف لاين\u00a0\u00a0—\u00a0\u00a0تفصيل حسب الطلب" : "RNF Line\u00a0\u00a0—\u00a0\u00a0Made to order"}
        </span>
        <h1 className="font-display text-[#F9F6F0] text-5xl sm:text-6xl lg:text-7xl leading-[1.4] pt-1 mb-8 max-w-3xl"
          style={{ textShadow: "0 2px 16px rgba(20,17,16,0.45)" }}>
          {ar ? <>أناقة تليق بكِ،<br />وتفاصيل تشبهكِ</> : <>Elegance that suits you,<br />details that feel like you</>}
        </h1>
        <div className="flex flex-wrap gap-3.5 mb-5">
          <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary px-10 py-4 text-base tracking-wide">{ar ? "تسوّقي المجموعة" : "Shop the Collection"}</Link>
          <Link to={`/product/${cur.id}`} data-testid="hero-order-btn"
            className="inline-flex items-center justify-center min-h-[48px] px-10 py-4 text-base tracking-wide border-[1.5px] border-[#F9F6F0] text-[#F9F6F0] hover:bg-[#F9F6F0] hover:text-[#141110] transition-colors">
            {ar ? "اطلبي حسب مقاسك" : "Made to Your Measurements"}
          </Link>
        </div>
        <Link to={`/product/${cur.id}`} data-testid="hero-model-link" className="text-[#F9F6F0]/90 text-base hover:text-[#B89C72] transition-colors">
          {ar ? cur.name_ar : (cur.name_en || cur.name_ar)} — <span className="font-num text-lg" dir="ltr">{formatKWD(cur.price, lang)}</span>
        </Link>
      </div>

      {n > 1 && (
        <>
          <button onClick={() => go(1)} data-testid="hero-prev" aria-label="السابق"
            className="absolute z-20 top-20 md:top-1/2 md:-translate-y-1/2 start-3 md:start-5 w-10 h-10 rounded-full bg-[#141110]/35 hover:bg-[#141110]/55 backdrop-blur-sm text-[#F9F6F0] flex items-center justify-center transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={() => go(-1)} data-testid="hero-next" aria-label="التالي"
            className="absolute z-20 top-20 md:top-1/2 md:-translate-y-1/2 end-3 md:end-5 w-10 h-10 rounded-full bg-[#141110]/35 hover:bg-[#141110]/55 backdrop-blur-sm text-[#F9F6F0] flex items-center justify-center transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="absolute z-20 bottom-6 inset-x-0 flex justify-center gap-2">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => { setPaused(true); setI(idx); }} data-testid={`hero-dot-${idx}`} aria-label={`شريحة ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-7 bg-[#B89C72]" : "w-2.5 bg-[#F9F6F0]/50 hover:bg-[#F9F6F0]/80"}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
